import {useCallback, useMemo} from 'react'

import {type InkVerdict} from '#/lib/inkVoice'
import {playInkVoice} from '#/lib/sounds'
import {useFlashBookSoundEffects} from '#/state/preferences'
import {
  useFlashBookPlayDesign,
  useIsFlashBookPlay,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {type FlashBookDesign} from '#/themes-flashbook/design'

/** How a block's answer came out, for the sound that goes with it. */
export type {InkVerdict}

/**
 * The paper sounds of a FlashBook: the nib of a highlighter, and the verdict on
 * what it marked.
 *
 * Deliberately *not* the app's generic `playCorrect` / `playWrong` chimes. A
 * book is paper, so its feedback is paper too — a stroke of the marker when a
 * learner chooses, a bright double stroke when that choice was right, one dull
 * falling stroke with the weight of the pen behind it when it was wrong.
 *
 * Which *paper* is the theme's, which is the whole reason this hook reads the
 * design: an exercise book is a soft pencil in a quiet room and a comic is a fat
 * marker over a zap, and one shared stroke for all nine themes was nine books
 * giving the same answer in the same voice. See
 * `#/themes-flashbook/inkVoices` for the voices and for the notebook's — the one
 * that was reported as loud and odd.
 *
 * Three rules, all of them in one place so no widget has to remember them:
 *
 *  1. **Only inside a book.** Outside one — capsule play, the capsule studio —
 *     this is a no-op, so the existing capsule feedback sounds stay the only
 *     ones the learner hears there.
 *  2. **Only when the reader wants them.** The preference is device-wide and
 *     defaults on; a silent book is one toggle away.
 *  3. **Never before a gesture.** Web Audio needs a user gesture to start, and
 *     every call here is made from a press, so the context is already unlocked
 *     by the time a sound is asked for.
 *
 * `inBook` can be asserted by the caller, and `FlashBookBlockPlayer` has to:
 * it is the component that *renders* the theme provider for its children, so the
 * context is not visible from its own body — the widgets below it hear the
 * marker stroke, and without this the verdict on top of it would be silent — and
 * the same goes for the design behind the voice, which it therefore passes in.
 * Anyone inside a book should leave both out and let the context answer.
 */
export function useFlashBookInkSound(
  options: {inBook?: boolean; design?: FlashBookDesign} = {},
) {
  const asserted = options.inBook
  const inContext = useIsFlashBookPlay()
  const inBook = asserted ?? inContext
  const enabled = useFlashBookSoundEffects()
  /*
   * The theme's voice, from the design rather than from a theme id: a design is
   * what every surface in a book already holds — the reader passes one down and the
   * studio's preview and the selector's miniature do too — so the sound follows the
   * book without a second lookup or a second context. The explicit `design` is for
   * `FlashBookBlockPlayer`, which *renders* the provider and so cannot read it.
   */
  const contextDesign = useFlashBookPlayDesign()
  const voice = (options.design ?? contextDesign).sound

  const mark = useCallback(() => {
    if (!inBook || !enabled) return
    playInkVoice(voice, 'partial')
  }, [inBook, enabled, voice])

  const judge = useCallback(
    (verdict: InkVerdict) => {
      if (!inBook || !enabled) return
      playInkVoice(voice, verdict)
    },
    [inBook, enabled, voice],
  )

  return useMemo(() => ({mark, judge}), [mark, judge])
}
